/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useRef, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { AlertBar } from '@blend/enterprise-components';

import { CommitmentCard } from './CommitmentCard';
import { DashboardSection } from '../DashboardSection';
import { DatesCard } from './DatesCard';

import { BlendBridge } from 'blb-types/blendBridge';
import { InstantTitleTrackerCard } from './InstantTitleTrackerCard';
import { OrderConfirmationCard } from './OrderConfirmationCard';
import { AgencyConfigurationWrapper } from '../AgencyConfigurationWrapper/AgencyConfigurationWrapper';
import { CommitmentUploadHeaderAction } from './CommitmentUploadHeaderAction';
import { TitleContextType } from './types';
import { CommitmentErrorMessage } from './constants';

export interface CurativeConfig {
  documentErrorText: CommitmentErrorMessage;
  documentViewText: string;
  documentUploadText: string;
  lienColumnNames: string[];
  requirementColumnNames: string[];
  requirementColumnWidths: number[];
  headerText: string;
  documentType: string;
  curativeStatus?: string;
}

export namespace TitleView {
  export interface Props {
    instantTitleActivity?: Title.Orders.InstantTitleActivity;
    loan: BlendBridge.API.Loan;
    order?: Title.Orders.Order;
  }
}

export const styles = StyleSheet.create({
  errorContainer: {
    marginBottom: 16,
  },
});

export const TitleContext = React.createContext<TitleContextType>({
  commitmentHeaderRef: null,
  loanType: undefined,
  instantTitleActivity: undefined,
  order: undefined,
  setCommitmentError: () => {},
  setInstantTitleActivity: () => {},
  setTitleOrder: () => {},
});

export const TitleTab: React.FC<TitleView.Props> = (props: TitleView.Props) => {
  const [order, setTitleOrder] = useState(props.order);
  const [commitmentError, setCommitmentError] = useState<string>('');
  const [instantTitleActivity, setInstantTitleActivity] = useState(props.instantTitleActivity);
  const commitmentHeaderRef = useRef<HTMLDivElement | null>(null);
  const loan = props.loan;
  const loanType = loan.loanType;
  const titleContextValue = {
    commitmentHeaderRef,
    loanType,
    instantTitleActivity,
    order,
    setCommitmentError,
    setInstantTitleActivity,
    setTitleOrder,
  };

  const homeEquityCurativeConfig: CurativeConfig = {
    documentErrorText: CommitmentErrorMessage.NO_REPORT,
    documentViewText: 'View Title Report',
    documentUploadText: 'Upload Title Report',
    lienColumnNames: ['Creditor Name', 'Type', 'Amount', 'Action'],
    requirementColumnNames: ['Description', 'Action'],
    requirementColumnWidths: [4, 1],
    headerText: 'Title Report',
    documentType: 'TITLE:_PROPERTY_REPORT',
    curativeStatus: 'CLEARED',
  };

  const mortgageCurativeConfig: CurativeConfig = {
    documentErrorText: CommitmentErrorMessage.NO_COMMITMENT,
    documentViewText: 'View Title Commitment',
    documentUploadText: 'Upload Title Commitment',
    lienColumnNames: ['Creditor Name', 'Type', 'Amount', 'Status', 'Action'],
    requirementColumnNames: ['Description', 'Status', 'Action'],
    requirementColumnWidths: [3, 1, 1],
    documentType: 'TITLE:COMMITMENT',
    headerText: 'Title Commitment',
  };

  const generateCurativeConfig = (loanType: string): CurativeConfig => {
    switch (loanType) {
      case 'HELOAN':
      case 'HELOC':
        return homeEquityCurativeConfig;
      default:
        return mortgageCurativeConfig;
    }
  };

  const curativeConfig: CurativeConfig = generateCurativeConfig(loanType);

  return (
    <TitleContext.Provider value={titleContextValue}>
      {order && !order.isConfirmedByLender && <OrderConfirmationCard />}
      {props.instantTitleActivity && (
        <DashboardSection
          sectionConfigs={[
            {
              content: <InstantTitleTrackerCard />,
              hideTitle: true,
              title: 'Instant Title Progress',
            },
          ]}
          sectionHeader="Instant Title Progress"
        />
      )}
      <DashboardSection
        sectionConfigs={[
          {
            content: <DatesCard />,
            hideTitle: true,
            title: 'Dates',
          },
        ]}
        sectionHeader="Dates"
      />
      {order && (
        <DashboardSection
          headerActions={<CommitmentUploadHeaderAction curativeConfig={curativeConfig} loan={loan} order={order} />}
          sectionConfigs={[
            {
              content: (
                <AgencyConfigurationWrapper hideDashboardSection section="titleCurative">
                  <div ref={commitmentHeaderRef}>
                    {!!commitmentError && (
                      <div className={css(styles.errorContainer)}>
                        <AlertBar icon="error" message={commitmentError} theme="error" />
                      </div>
                    )}
                    <CommitmentCard curativeConfig={curativeConfig} />
                  </div>
                </AgencyConfigurationWrapper>
              ),
              hideTitle: true,
              title: 'Title Commitment',
            },
          ]}
          sectionHeader={curativeConfig.headerText}
          wideCard
        />
      )}
    </TitleContext.Provider>
  );
};
